// İSG uzmanı portalı — Araç Sheet'inin belge listesi (ana firma aktörü; §23). Araç kaydının
// belgeleri `ContractorVehicleDto.documents` / `WorkPermitVehicleDto.documents` içinde GÖMÜLÜ
// gelir; ayrı sorgu yok. Onay/red/silme HER izinde geçerlidir → `invalidateVehicleCaches`.
// Silmeden sonra odak: silinen satırın tetikleyicisi kalkar; odak durumla "Belge yükle"ye iner
// (zamanlayıcı DEĞİL — Modal'ın kapanış odak geri yüklemesi bittikten sonra, `PersonnelPanel.tsx` deseni).
// Başarısız onay/red satırı kaldırırsa (belge silinmiş) hook aynı hedefe iner; damgalar `source`'tan.

import {
  Button,
  ConfirmDialog,
  type ContractorVehicleDto,
  DocumentRow,
  EmptyState,
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableMessageRow,
  TableRow,
  type VehicleDocumentDto,
  canDeleteDocument,
  currentCulture,
  documentNameDetail,
  documentValidityText,
  presentDocument,
  useDocumentTypes,
  vehiclesApi,
} from '@isgportal/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FileText, Trash2, Upload } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../../lib/format';
import { invalidateVehicleCaches } from '../../lib/vehicleCache';
import {
  DocumentRejectRow,
  DocumentReviewActions,
  DocumentReviewFeedback,
  type ReviewSource,
  documentNameLabel,
  useDocumentReview,
} from '../documents/DocumentReviewActions';
import { VehicleDocumentLinkModal } from './VehicleDocumentLinkModal';

interface VehicleDocumentsProps {
  contractorUuid: string;
  vehicle: Pick<ContractorVehicleDto, 'uuid' | 'plate' | 'documents'>;
  /** Aracı (ve gömülü belgelerini) getiren sorgunun damgaları: kayıt listesi ya da izin detayı. */
  source: ReviewSource;
}

export function VehicleDocuments({ contractorUuid, vehicle, source }: VehicleDocumentsProps) {
  const { t } = useTranslation();
  const { label } = useDocumentTypes();
  const culture = currentCulture();
  const queryClient = useQueryClient();
  const [uploadOpen, setUploadOpen] = useState(false);
  const [toDelete, setToDelete] = useState<VehicleDocumentDto | null>(null);
  const [deletedUuid, setDeletedUuid] = useState<string | null>(null);
  const uploadButtonRef = useRef<HTMLButtonElement>(null);
  const documents = vehicle.documents;

  const review = useDocumentReview({
    approve: (id) => vehiclesApi.approveVehicleDocument(contractorUuid, vehicle.uuid, id),
    reject: (id, reason) =>
      vehiclesApi.rejectVehicleDocument(contractorUuid, vehicle.uuid, id, { reason }),
    invalidate: () => invalidateVehicleCaches(queryClient, contractorUuid),
    documents,
    source,
    fallbackFocusRef: uploadButtonRef,
    overlayOpen: uploadOpen || toDelete !== null,
  });

  const deleteMutation = useMutation({
    mutationFn: (documentUuid: string) => {
      review.clearFeedback();
      return vehiclesApi.deleteVehicleDocument(contractorUuid, vehicle.uuid, documentUuid);
    },
    onSuccess: (_data, documentUuid) => {
      invalidateVehicleCaches(queryClient, contractorUuid);
      setDeletedUuid(documentUuid);
      setToDelete(null);
    },
    // 409 VEHICLE_DOCUMENT_IN_USE: bayat olgu — kutuda mesaj kalır, liste tazelenir.
    // Satır/tetikleyici yenilemeyle kalkarsa odak yine "Belge yükle"ye insin (aşağıdaki efekt).
    onError: (_error, documentUuid) => {
      invalidateVehicleCaches(queryClient, contractorUuid);
      setDeletedUuid(documentUuid);
    },
  });

  // Silinen satır listeden KALKINCA ve diyalog kapanınca odak <body>'ye düşmesin.
  useEffect(() => {
    if (
      deletedUuid !== null &&
      toDelete === null &&
      !documents.some((doc) => doc.uuid === deletedUuid)
    ) {
      uploadButtonRef.current?.focus();
      setDeletedUuid(null);
    }
  }, [documents, deletedUuid, toDelete]);

  const title = t('vehicles.sheet.docs.title');

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold text-fg">{title}</h2>
        <Button
          ref={uploadButtonRef}
          variant="secondary"
          size="sm"
          onClick={() => setUploadOpen(true)}
        >
          <Upload className="h-4 w-4" aria-hidden="true" />
          {t('vehicles.sheet.docs.upload')}
        </Button>
      </div>

      <DocumentReviewFeedback review={review} />

      <Table caption={title}>
        <TableHeader>
          <TableRow className="carbon:h-0">
            <TableHead className="h-0 p-0 carbon:h-0">
              <span className="sr-only">{t('permits.vehicles.docs.col.type')}</span>
            </TableHead>
            <TableHead className="h-0 p-0 text-right carbon:h-0">
              <span className="sr-only">{t('contractors.common.actions')}</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {documents.length === 0 ? (
            <TableMessageRow colSpan={2}>
              <EmptyState icon={FileText} title={t('vehicles.sheet.docs.empty')} />
            </TableMessageRow>
          ) : (
            documents.map((doc) => {
              const typeLabel = label('vehicle', doc.type);
              const deletable = canDeleteDocument(doc, 'host');
              const p = presentDocument(doc, t);
              const validity = documentValidityText(doc, (iso) => formatDate(iso, culture), t);
              const nameDetail = documentNameDetail(doc, validity);
              const deleteName = t('documents.review.delete', {
                type: documentNameLabel(typeLabel, nameDetail),
              });
              return (
                <DocumentRow
                  key={doc.uuid}
                  title={typeLabel}
                  fileName={doc.fileName}
                  // #17: araç yüklemeleri doc-check'ten geçene dek `recognitionRelease` hep null;
                  // tanıma işareti (`RecognitionMark` → `provenance`) o zaman eklenir.
                  noteText={doc.note}
                  validityRangeText={validity}
                  quiet={p.quiet}
                  quietLabel={p.quietLabel}
                  tags={p.tags}
                  srExtra={p.srExtra}
                  statusContextLabel={t('permits.vehicles.docs.col.approval')}
                  rejectionReasonText={
                    doc.approvalStatus === 'Rejected' && doc.rejectionReason
                      ? t('documents.review.rejectionReason', { reason: doc.rejectionReason })
                      : null
                  }
                  lockedText={deletable ? null : t('vehicles.doc.inUse')}
                  actions={
                    <DocumentReviewActions
                      review={review}
                      document={{
                        uuid: doc.uuid,
                        documentUuid: doc.documentUuid,
                        approvalStatus: doc.approvalStatus,
                        typeLabel,
                        nameDetail,
                      }}
                      deleteSlot={
                        deletable ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setToDelete(doc)}
                            aria-label={deleteName}
                            title={deleteName}
                          >
                            <Trash2 className="h-4 w-4 text-danger" aria-hidden="true" />
                          </Button>
                        ) : null
                      }
                    />
                  }
                  editorRow={
                    <DocumentRejectRow review={review} documentUuid={doc.uuid} colSpan={2} />
                  }
                />
              );
            })
          )}
        </TableBody>
      </Table>
      <p className="text-xs text-muted">{t('vehicles.sheet.docs.note')}</p>

      <VehicleDocumentLinkModal
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        contractorUuid={contractorUuid}
        vehicleUuid={vehicle.uuid}
        vehiclePlate={vehicle.plate}
      />
      <ConfirmDialog
        open={toDelete !== null}
        onOpenChange={(open) => {
          if (!open) {
            setToDelete(null);
            deleteMutation.reset();
            // Satır hâlâ listedeyse bayat odak hedefi bırakma.
            if (toDelete && documents.some((doc) => doc.uuid === toDelete.uuid)) {
              setDeletedUuid(null);
            }
          }
        }}
        title={t('vehicles.doc.delete.title')}
        body={t('vehicles.doc.delete.body', {
          type: toDelete ? label('vehicle', toDelete.type) : '',
        })}
        confirmLabel={t('vehicles.doc.delete.confirm')}
        loading={deleteMutation.isPending}
        error={deleteMutation.isError ? deleteMutation.error : undefined}
        onConfirm={() => toDelete && deleteMutation.mutate(toDelete.uuid)}
      />
    </div>
  );
}
